import { useRef, useState, useEffect, useCallback } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Typography, CircularProgress, Alert, Snackbar, Box, Button } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import { isAxiosError } from 'axios';

import { config } from '@/config';
import {
  useAppraisalDetail,
  useImportFromAi,
  useExportForAi,
  useInterviewSummary,
} from '@/queries/useAppraisalDetail';
import { useAppraisalAccess } from '@/queries/usePermissions';
import { ImportAiDialog } from './ImportAiDialog';
import type { AiImportRequest } from '@/api/types/appraisal.types';
import { generateAppraisalPdf } from '@/utils/generateAppraisalPdf';
import {
  NO_ERRORS,
  getErrorMessages,
  validateForHandover,
  validateForSubmitToTc,
} from '@/components/appraisals/appraisalValidation';
import type { AppraisalValidationErrors } from '@/components/appraisals/appraisalValidation';
import { AppraisalFooter } from '@/components/appraisals/AppraisalFooter';
import { AppraisalValidationBanner } from '@/components/appraisals/AppraisalValidationBanner';
import { ViewPriorEvaluationButton } from '@/components/appraisals/ViewPriorEvaluationButton';
import { AppraisalPdfContent } from '@/components/appraisals/AppraisalPdfContent';
import { AppraisalDetailsSection } from './AppraisalDetailsSection';
import { ProjectHistorySection } from './ProjectHistorySection';
import { InterviewSummarySection } from './InterviewSummarySection';
import { SkillAreaStrengthsSection } from './SkillAreaStrengthsSection';
import { SelfAssessmentSection } from './SelfAssessmentSection';
import { ForwardLookingPlanSection } from './ForwardLookingPlanSection';
import { PerformanceSummarySection } from './PerformanceSummarySection';
import { NotesToTalentCommitteeSection } from './NotesToTalentCommitteeSection';
import { NotesActionsForCollatorSection } from './NotesActionsForCollatorSection';
import { StaffingTcNotesSection } from './StaffingTcNotesSection';
import { UpwardFeedbackSection } from './UpwardFeedbackSection';
import { AuditHistorySection } from './AuditHistorySection';

export function AppraisalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: appraisalDetail, isLoading, error } = useAppraisalDetail(id || '');
  const { data: permissions, isError: isPermissionsError, error: permissionsError } = useAppraisalAccess(id || '');
  const { data: interviewSummaryEntries = [] } = useInterviewSummary(Number(id));

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const [validationErrors, setValidationErrors] = useState<AppraisalValidationErrors>(NO_ERRORS);
  const [validationSource, setValidationSource] = useState<'handover' | 'submitToTc' | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const [scrollTrigger, setScrollTrigger] = useState(0);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const importMutation = useImportFromAi();
  const exportMutation = useExportForAi();

  // Scroll to top when validation errors are triggered
  useEffect(() => {
    if (scrollTrigger === 0) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [scrollTrigger]);

  const handleValidationErrors = useCallback(
    (errors: AppraisalValidationErrors, source: 'handover' | 'submitToTc' | null) => {
      setValidationErrors(errors);
      setValidationSource(source);
      const hasAnyError = Object.values(errors).some(Boolean);
      if (hasAnyError) {
        setScrollTrigger((prev) => prev + 1);
      }
    },
    [],
  );

  const handleDownloadPdf = useCallback(() => {
    if (!appraisalDetail) return;
    setIsGeneratingPdf(true);
  }, [appraisalDetail]);

  // Generate PDF after content is rendered off-screen
  useEffect(() => {
    if (!isGeneratingPdf || !pdfContentRef.current || !appraisalDetail) return;
    const timer = setTimeout(async () => {
      try {
        const periodEnd = appraisalDetail.appraisalPeriodEnd
          ? new Date(appraisalDetail.appraisalPeriodEnd).getFullYear().toString()
          : 'Current';
        const filename = `Appraisal_${appraisalDetail.appraiseeFirstName}_${appraisalDetail.appraiseeLastName}_${periodEnd}.pdf`;
        await generateAppraisalPdf(pdfContentRef.current!, filename);
      } catch {
        setSnackbar({ open: true, message: 'Failed to generate PDF', severity: 'error' });
      } finally {
        setIsGeneratingPdf(false);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [isGeneratingPdf, appraisalDetail]);

  // AC5: auto-dismiss validation banner as the user resolves errors.
  // Sections auto-save on blur, which invalidates appraisalDetail. When fresh
  // data arrives, re-run the matching validator so the banner clears reactively.
  useEffect(() => {
    if (!validationSource || !appraisalDetail) return;
    const next = validationSource === 'handover'
      ? validateForHandover(appraisalDetail)
      : validateForSubmitToTc(appraisalDetail, interviewSummaryEntries);
    setValidationErrors(next);
  }, [validationSource, appraisalDetail, interviewSummaryEntries]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !appraisalDetail || !id || Number.isNaN(Number(id))) {
    return <Navigate to="/error" replace />;
  }

  const errorMessages = validationSource
    ? getErrorMessages(validationErrors, validationSource)
    : [];

  const handoverTriggered = validationSource === 'handover';
  const submitTcTriggered = validationSource === 'submitToTc';

  return (
    <>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        APPRAISALS - {appraisalDetail.appraiseeFirstName} {appraisalDetail.appraiseeLastName}
      </Typography>

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
        <ViewPriorEvaluationButton appraisalId={String(id)} />
        {permissions?.actions?.canImportFromAi && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<UploadFileIcon />}
            onClick={() => { setImportError(null); setImportDialogOpen(true); }}
          >
            Import from AI
          </Button>
        )}
        {permissions?.actions?.canExportToAi && (
          <Button
            variant="contained"
            color="secondary"
            disabled={exportMutation.isPending}
            startIcon={exportMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            onClick={async () => {
              try {
                await exportMutation.mutateAsync({
                  appraisalId: Number(id),
                  firstName: appraisalDetail.appraiseeFirstName ?? '',
                  lastName: appraisalDetail.appraiseeLastName ?? '',
                });
                setSnackbar({ open: true, message: 'AI export downloaded successfully.', severity: 'success' });
              } catch {
                setSnackbar({ open: true, message: 'Failed to export AI data. Please try again.', severity: 'error' });
              }
            }}
          >
            {exportMutation.isPending ? 'Exporting...' : 'Export to AI'}
          </Button>
        )}
      </Box>

      <AppraisalValidationBanner errors={errorMessages} />

      {isPermissionsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load permissions: {permissionsError?.message || 'Unknown error'}
        </Alert>
      )}
      <AppraisalDetailsSection
        appraisalDetail={appraisalDetail}
        permissions={permissions}
      />
      <ProjectHistorySection
        appraisalDetail={appraisalDetail}
        permissions={permissions}
      />
      {permissions?.sections.interviewSummary && (
        <InterviewSummarySection
          appraisalId={appraisalDetail.id}
          permissions={permissions}
          validationTriggered={submitTcTriggered}
        />
      )}
      {permissions?.sections.selfAssessment && (
        <SelfAssessmentSection
          appraisalDetail={appraisalDetail}
          permissions={permissions}
          validationTriggered={handoverTriggered}
        />
      )}
      {permissions?.sections.forwardLookingPlan && (
        <ForwardLookingPlanSection
          appraisalDetail={appraisalDetail}
          permissions={permissions}
          validationTriggered={submitTcTriggered}
        />
      )}
      {config.powerBiUpwardFeedbackEnabled && permissions?.sections.upwardFeedback?.access === 'read' && (
        <UpwardFeedbackSection
          appraisalId={appraisalDetail.id}
          appraiseeBambooId={appraisalDetail.appraiseeBambooId}
        />
      )}
      {permissions?.sections.skillAreaStrengths && (
        <SkillAreaStrengthsSection
          appraisalDetail={appraisalDetail}
          permissions={permissions}
          validationTriggered={submitTcTriggered}
        />
      )}
      {permissions?.sections.performanceSummary && (
        <PerformanceSummarySection
          appraisalDetail={appraisalDetail}
          permissions={permissions}
          validationTriggered={submitTcTriggered}
        />
      )}
      {permissions?.sections.notesToTalentCommittee && (
        <NotesToTalentCommitteeSection
          appraisalDetail={appraisalDetail}
          permissions={permissions}
          validationTriggered={submitTcTriggered}
        />
      )}
      {permissions?.sections.notesActionsForCollator && (
        <NotesActionsForCollatorSection appraisalDetail={appraisalDetail} permissions={permissions} />
      )}
      {permissions?.sections.staffingTcNotes && (
        <StaffingTcNotesSection appraisalDetail={appraisalDetail} />
      )}
      {permissions?.sections.auditHistory?.access === 'read' && (
        <AuditHistorySection appraisalId={id || ''} />
      )}

      <AppraisalFooter
        appraisalId={String(id)}
        permissions={permissions}
        appraisalDetail={appraisalDetail}
        interviewSummaryEntries={interviewSummaryEntries}
        setSnackbar={setSnackbar}
        setValidationErrors={handleValidationErrors}
        onDownloadPdf={handleDownloadPdf}
        isGeneratingPdf={isGeneratingPdf}
      />

      {isGeneratingPdf && (
        <div
          ref={pdfContentRef}
          style={{ position: 'fixed', left: '-9999px', top: 0, width: '794px' }}
        >
          <AppraisalPdfContent
            appraisal={appraisalDetail}
            permissions={permissions}
            interviewSummaryEntries={interviewSummaryEntries}
          />
        </div>
      )}

      <ImportAiDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={async (data: AiImportRequest) => {
          try {
            await importMutation.mutateAsync({
              appraisalId: Number(id),
              data: { ...data, rowVersion: appraisalDetail.rowVersion },
            });
            setImportDialogOpen(false);
            setSnackbar({ open: true, message: 'AI results imported successfully.', severity: 'success' });
          } catch (error) {
            if (isAxiosError(error) && error.response?.status === 409) {
              // Concurrency conflict: global Axios interceptor shows the "modified by another user" toast;
              // useImportFromAi.onError invalidates the cache. Exit the dialog per concurrency-control standard.
              setImportDialogOpen(false);
              return;
            }
            setImportError('AI import failed. Please try again or contact support if the problem persists.');
            throw error;
          }
        }}
        isLoading={importMutation.isPending}
        error={importError}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
}

export default AppraisalDetailPage;
